import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { formatCents, formatCount } from '@affily/shared/money';
import { api } from '../lib/api.js';
import { useFetch } from '../hooks/useFetch.js';
import { Button, Alert, SectionTitle } from './ui.jsx';

/**
 * Brand payment method.
 *
 * Nothing is charged here — this saves a method so a payout can charge it later
 * when nobody is at a keyboard. Affily does not front commissions: a creator is
 * not paid until the brand's money has cleared, so without this on file the
 * settle button leads nowhere.
 *
 * The card is collected on Stripe's own hosted Checkout page: the brand is sent
 * there and returned with `?session_id=`, which we confirm server-side. No card
 * field ever touches our origin, so we stay out of PCI scope entirely, and no
 * Stripe.js has to be loaded here at all.
 */
export function BillingSetup({ compact = false }) {
  const { data, loading, reload } = useFetch('/stripe/billing');
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState('');
  const [params, setParams] = useSearchParams();
  const confirmed = useRef(null);

  const ready = data?.ready;
  const owed = data?.owed ?? { grossCents: 0, count: 0 };
  const declined = (data?.billingFailures ?? 0) > 0;

  // Back from Checkout: the URL carries the session, and we ask the server to
  // verify it with Stripe rather than trusting the browser's say-so. The ref
  // keeps a double-invoked effect from confirming twice.
  const sessionId = params.get('session_id');
  useEffect(() => {
    if (!sessionId || confirmed.current === sessionId) return;
    confirmed.current = sessionId;

    (async () => {
      setConfirming(true);
      setError('');
      try {
        await api.post('/stripe/billing/confirm', { sessionId });
        reload();
      } catch (err) {
        setError(err.message);
      } finally {
        setConfirming(false);
        // Drop the id either way, so a refresh doesn't replay a spent session.
        setParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.delete('session_id');
            return next;
          },
          { replace: true },
        );
      }
    })();
  }, [sessionId, reload, setParams]);

  async function addPaymentMethod() {
    setBusy(true);
    setError('');
    try {
      const { url } = await api.post('/stripe/billing/setup');
      window.location.assign(url);
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  }

  if (loading) return null;

  if (!data?.stripeConfigured) {
    return (
      <Alert tone="caution">
        Payouts aren’t configured on this server yet. Commissions still accrue and are recorded —
        nothing is lost.
      </Alert>
    );
  }

  // A declined card is the one thing here a brand must act on, so it shows
  // wherever billing does — including the compact settle-screen banner.
  const declinedAlert = declined && (
    <Alert tone="caution" className="mb-3">
      Your card was declined the last time we tried to pay a creator, so they have not been paid.
      Their commissions are safe and still settled — replace your card, or re-authenticate it, and
      run the payout again.
      {data.billingFailures >= 3 &&
        ' Because this has now failed several times, auto-approval is switched off on your programs until it’s fixed.'}
    </Alert>
  );

  if (ready) {
    if (compact) return declinedAlert || null;
    return (
      <section>
        <SectionTitle>Payment method</SectionTitle>
        {declinedAlert}
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-positive/25 bg-positive-soft p-4">
          <div>
            <p className="font-medium text-positive">{data.paymentMethod}</p>
            <p className="mt-0.5 text-sm text-ink-muted">
              Charged when you settle and pay a creator. Never before.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={addPaymentMethod} disabled={busy || confirming}>
            {confirming ? 'Saving…' : busy ? 'Opening…' : 'Replace'}
          </Button>
        </div>
        <Alert className="mt-3">{error}</Alert>
      </section>
    );
  }

  return (
    <section className="rounded-lg border border-caution/30 bg-caution-soft p-5">
      <h2 className="font-semibold text-caution">Add a payment method to pay creators</h2>
      <p className="mt-1.5 max-w-xl text-sm text-ink-muted">
        Affily doesn’t front commissions. Your card is charged for the commission you owe, and only
        once that clears does the creator get paid — so nothing can be settled until there is a
        method on file.
      </p>

      {owed.count > 0 && (
        <p className="mt-3 text-sm">
          <span className="font-semibold text-ink">
            {(owed.byCurrency?.length ? owed.byCurrency : [owed])
              .map((o) => formatCents(o.totalCents ?? o.grossCents, { currency: o.currency }))
              .join(' + ')}
          </span>{' '}
          <span className="text-ink-muted">
            is currently owed across {formatCount(owed.count)} commission
            {owed.count === 1 ? '' : 's'} — work creators have already done.
          </span>
        </p>
      )}

      <Button onClick={addPaymentMethod} disabled={busy || confirming} className="mt-4">
        {confirming ? 'Saving your card…' : busy ? 'Opening Stripe…' : 'Add payment method'}
      </Button>
      <p className="mt-2 text-xs text-ink-faint">
        Handled entirely by Stripe. Card details never touch Affily.
      </p>
      <Alert className="mt-3">{error}</Alert>
    </section>
  );
}
